import React from 'react';

import type { DebounceSettings, DebounceSettingsLeading, DebouncedFuncLeading } from 'lodash';
import type { DebouncedFunc } from 'lodash-es';
import { debounce } from 'lodash-es';

import type { RestParameters } from '../../utils/types';
import { useIsMountedRef } from './use-is-mounted-ref';

/**
 * 입력으로 받은 callback을 debounce 처리합니다.
 *
 * @see https://www.developerway.com/posts/debouncing-in-react
 */
export const useDebounce: {
  <T extends (...args: any) => any>(
    callback: T,
    wait?: number,
    options?: DebounceSettingsLeading,
  ): DebouncedFuncLeading<T>;
  <T extends (...args: any) => any>(callback: T, wait?: number, options?: DebounceSettings): DebouncedFunc<T>;
} = <T extends (...args: any) => any>(
  callback: T,
  wait = 300,
  options: DebounceSettingsLeading | DebounceSettings = { leading: true, trailing: false },
) => {
  const ref = React.useRef<(...args: Parameters<T>) => ReturnType<T>>(callback);
  ref.current = callback;

  return React.useMemo(
    () => {
      const func = (...args: Parameters<T>) => {
        return ref.current(...args);
      };

      return debounce(func, wait, options);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      // wait,
      // options,
    ],
  );
};

/**
 * 값을 Debounce 처리 합니다.
 *
 * @param nextValue 다음 value.
 * @returns throttle 된 value.
 */
export const useDebounceValue = <T>(
  nextValue: T,
  wait?: number,
  options?: DebounceSettingsLeading | DebounceSettings,
) => {
  const [value, setValue] = React.useState(nextValue);

  const lazySetValue = useDebounce(setValue, wait, options);

  const nextValueRef = React.useRef(nextValue);

  if (nextValueRef.current !== nextValue) {
    nextValueRef.current = nextValue;
    lazySetValue(nextValue);
  }

  return value;
};

/**
 * 입력으로 받은 async callback을 debounce 처리 하면서, 진행 중 여부를 제공합니다.
 *
 * @see https://www.developerway.com/posts/debouncing-in-react
 */
export const useDebounceAction: {
  <T extends (...args: any) => Promise<any>>(
    callback: T,
    wait?: number,
    options?: DebounceSettingsLeading,
  ): {
    callback: DebouncedFuncLeading<T>;
    isRunning: boolean;
  };
  <T extends (...args: any) => Promise<any>>(
    callback: T,
    wait?: number,
    options?: DebounceSettings,
  ): {
    callback: DebouncedFunc<T>;
    isRunning: boolean;
  };
} = <T extends (...args: any) => R, R extends Promise<any>>(
  callback: T,
  wait = 300,
  options: DebounceSettingsLeading | DebounceSettings = { leading: true, trailing: false },
) => {
  const mountedRef = useIsMountedRef();

  const ref = React.useRef<(...args: Parameters<T>) => R>(callback);
  ref.current = callback;

  const [isPending, setPending] = React.useState(false);

  const debounceCallback = React.useMemo(
    () => {
      const func = async (...args: Parameters<T>) => {
        try {
          setPending(true);
          return await ref.current(...args);
        } finally {
          if (mountedRef.current.isMounted) {
            setPending(false);
          }
        }
      };

      return debounce(func, wait, options);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      // wait,
      // options,
    ],
  );

  return {
    /**
     * debouce 된 callback.
     */
    callback: debounceCallback,

    /**
     * async callback 이 진행 중이면 true.
     */
    isPending,

    /**
     * @deprecated isPending 사용.
     */
    isRunning: isPending,
  };
};

/**
 * React.useActionState 와 비슷하게, debounce 를 사용하여 async 액션을 처리합니다.
 */
export const useDebounceActionState: {
  <T extends (prevState: S, ...args: any) => Promise<S>, S>(
    action: T,
    initialState: S | (() => S),
    wait?: number,
    options?: DebounceSettingsLeading,
  ): [S, DebouncedFuncLeading<(...args: RestParameters<T>) => void>, boolean];
  <T extends (prevState: S, ...args: any) => Promise<S>, S>(
    action: T,
    initialState: S | (() => S),
    wait?: number,
    options?: DebounceSettings,
  ): [S, DebouncedFunc<(...args: RestParameters<T>) => void>, boolean];
} = <T extends (prevState: S, ...args: any) => R, R extends Promise<S>, S>(
  action: T,
  initialState: S | (() => S),
  wait = 300,
  options: DebounceSettingsLeading | DebounceSettings = { leading: true, trailing: false },
) => {
  const mountedRef = useIsMountedRef();

  const [isPending, setPending] = React.useState(false);
  const [state, setState] = React.useState(initialState);

  const ref = React.useRef<{ action: (prevState: S, ...args: RestParameters<T>) => R; payload: S }>({
    action,
    payload: state,
  });
  ref.current.action = action;
  ref.current.payload = state;

  const debounceCallback = React.useMemo(
    () => {
      const func = (...args: RestParameters<T>) => {
        setPending(true);
        ref.current
          .action(ref.current.payload, ...args)
          .then(value => {
            if (mountedRef.current.isMounted) {
              setState(value);
              setPending(false);
            }
          })
          .catch(() => {
            if (mountedRef.current.isMounted) {
              setPending(false);
            }
          });
      };

      return debounce(func, wait, options);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      // wait,
      // options,
    ],
  );

  return [
    state,

    /**
     * debouce 된 callback.
     */
    debounceCallback,

    /**
     * async callback 이 진행 중이면 true.
     */
    isPending,
  ];
};
